import React, { ReactNode } from 'react';
import { Stack, Text, Image, Link } from '@fluentui/react';
import { Badge, Button } from '@fluentui/react-components';
import { QRCodeSVG } from 'qrcode.react';
import { UserContext } from '@/UserContext';
import microsoftLogo from '@/assets/microsoftLogo.png';
import { useTenantLogo } from '../useTenantLogo';
import { BackButton } from './BackButton';
import '@/styles/common/card-layout.css';
import '@/styles/common/page.css';

const visuallyHiddenStyle: React.CSSProperties = {
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: '1px',
  margin: '-1px',
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  width: '1px',
  whiteSpace: 'nowrap'
};

export interface InstructionStep {
  number: string | number;
  text: string;
}

export interface CardScreenProps {
  /** Main title of the screen */
  title: string;
  /** Optional subtitle below the title */
  subtitle?: string;
  /** Optional QR code URL. If provided, displays QR code */
  qrCodeUrl?: string;
  /** Optional QR code size. Defaults to 160 */
  qrCodeSize?: number;
  /** Optional logo to display on top of QR code (e.g., teams.png, authenticator.png) */
  qrLogoUrl?: string;
  /** Optional accessible label announced for the QR code */
  qrCodeAriaLabel?: string;
  /** Optional list of instruction steps */
  instructions?: InstructionStep[];
  /** Primary button text */
  primaryButtonText?: string;
  /** Primary button click handler */
  onPrimaryButtonClick?: () => void;
  /** Optional secondary link text (e.g., "Can't scan QR code?") */
  secondaryLinkText?: string;
  /** Optional secondary link URL */
  secondaryLinkUrl?: string;
  /** Optional accessible label for the secondary link */
  secondaryLinkAriaLabel?: string;
  /** Whether to auto-focus the primary button on initial render */
  autoFocusPrimaryButton?: boolean;
  /** Optional element id to receive initial focus when autoFocusPrimaryButton is false */
  initialFocusElementId?: string;
  /** Custom onClick handler for back button. If not provided, uses default navigate(-1) */
  onBackClick?: () => void;
  /** Whether to show the back button. Defaults to true */
  showBackButton?: boolean;
  /** Whether to show the user info (email/username). Defaults to true */
  showUserInfo?: boolean;
  /** Whether to show the logo at the top. Defaults to true */
  showLogo?: boolean;
  /** Additional custom content to render below the standard elements */
  children?: ReactNode;
  /** Custom logo alt text. Defaults to "Company logo" */
  logoAltText?: string;
}

/**
 * Reusable card-based screen layout component
 * Provides consistent structure for onboarding screens with optional QR codes and instructions
 */
export const CardScreen: React.FC<CardScreenProps> = ({
  title,
  subtitle,
  qrCodeUrl,
  qrCodeSize = 160,
  qrLogoUrl,
  qrCodeAriaLabel,
  instructions,
  primaryButtonText,
  onPrimaryButtonClick,
  secondaryLinkText,
  secondaryLinkUrl,
  secondaryLinkAriaLabel,
  autoFocusPrimaryButton = true,
  initialFocusElementId,
  onBackClick,
  showBackButton = true,
  showUserInfo = true,
  showLogo = true,
  children,
  logoAltText = "Company logo"
}) => {
  const user = React.useContext(UserContext);
  console.log("CardScreen user context:", user);
  const logoUrl = useTenantLogo();
  const primaryButtonRef = React.useRef<HTMLButtonElement | null>(null);
  const resolvedQrCodeLabel = qrCodeAriaLabel ?? instructions?.[0]?.text;
  const shouldAutoFocusPrimary = Boolean(autoFocusPrimaryButton && primaryButtonText && onPrimaryButtonClick);
  const hasSetInitialFocus = React.useRef(false);
  const titleRef = React.useRef<HTMLHeadingElement | null>(null);
  const baseId = React.useId();
  const titleId = `${baseId}-title`;
  const instructionsId = instructions && instructions.length > 0 ? `${baseId}-instructions` : undefined;
  const announcementText = React.useMemo(() => {
    const instructionSummary = instructions?.map((instruction) => `${instruction.number}. ${instruction.text}`).join(' ');
    return [title, instructionSummary].filter(Boolean).join(' ');
  }, [instructions, title]);
  const announcementId = announcementText ? `${baseId}-announcement` : undefined;

  React.useEffect(() => {
    if (hasSetInitialFocus.current) {
      return;
    }

    if (shouldAutoFocusPrimary && primaryButtonRef.current) {
      primaryButtonRef.current.focus();
      hasSetInitialFocus.current = true;
      return;
    }

    if (initialFocusElementId) {
      const focusTarget = document.getElementById(initialFocusElementId);
      if (focusTarget instanceof HTMLElement) {
        focusTarget.focus();
        hasSetInitialFocus.current = true;
        return;
      }
    }

    if (titleRef.current) {
      titleRef.current.focus();
      hasSetInitialFocus.current = true;
    }
  }, [initialFocusElementId, shouldAutoFocusPrimary]);

  return (
    <Stack className="page-bg screen-background">
      <Stack className="screen-center">
        <Stack className="screen-card">

          {showLogo && (
            <Image
              src={logoUrl || microsoftLogo}
              alt={logoAltText}
              className="screen-logo"
            />
          )}
          
          {showUserInfo && user && (user.username || user.email || user.name) && (
            <Stack className="screen-user-info">
              <Text variant="small">{user.username || user.email || user.name}</Text>
            </Stack>
          )}
          
          <h1
                       id={titleId}
                       tabIndex={-1}
                       ref={titleRef}
                       className="screen-title"
                     >
                       {title}
                     </h1>
          
          {subtitle && (
            <Text variant="medium" className="screen-subtitle">{subtitle}</Text>
          )}
          
          {qrCodeUrl && (
            <Stack horizontal horizontalAlign="center" verticalAlign="center" className="screen-qr-container">
               <div className="qr-code-wrapper">
              <QRCodeSVG
                value={qrCodeUrl}
                size={qrCodeSize}
                role={resolvedQrCodeLabel ? 'img' : undefined}
                aria-label={resolvedQrCodeLabel}
                aria-hidden={resolvedQrCodeLabel ? undefined : true}
              />
              {qrLogoUrl && (
                <img src={qrLogoUrl} alt="QR Logo" className="qr-teams-logo" />
              )}
              </div>
            </Stack>
          )}
          
          {instructions && instructions.length > 0 && (
            <ol
              className="screen-instructions"
              id={instructionsId}
            >
              {instructions.map((instruction, index) => (
                <li className="instruction-step" key={index}>
                  <Badge 
                    appearance="filled" 
                    color="brand"
                    size="large"
                    className="step-badge"
                  >
                    {instruction.number}
                  </Badge>
                  <Text variant="medium">{instruction.text}</Text>
                </li>
              ))}
            </ol>
          )}
          
          {children}
          
          {primaryButtonText && onPrimaryButtonClick && (
            <>
              {announcementId && (
                <span id={announcementId} style={visuallyHiddenStyle}>
                  {announcementText}
                </span>
              )}
            <Button
              appearance="primary"
              onClick={onPrimaryButtonClick}
              className="screen-primary-button"
              ref={primaryButtonRef}
              aria-describedby={announcementId}
            >
              {primaryButtonText}
            </Button>
            </>
          )}
          
          {secondaryLinkText && secondaryLinkUrl && (
            <Stack className="screen-link-container">
              <Link
                href={secondaryLinkUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="screen-link"
                aria-label={secondaryLinkAriaLabel}
              >
                {secondaryLinkText}
              </Link>
            </Stack>
          )}

          {showBackButton && <BackButton onClick={onBackClick} /> }
        </Stack>
      </Stack>
    </Stack>
  );
};

export default CardScreen;